// Horizontal R-multiple bar showing entry → stop → current → optional 2R target.
// Distance-to-stop is "1R"; everything else is measured in units of R.
// Edgewonk / Tradervue convention.

import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { fmtNumber, fmtSignedPercent } from '@/lib/format';

type Props = {
  entry: number;
  stop?: number;
  current: number;
  targetR?: number; // default 2R
};

export function RMultipleBar({ entry, stop, current, targetR = 2 }: Props) {
  const view = useMemo(() => {
    if (!stop || stop <= 0 || !Number.isFinite(stop)) {
      return null;
    }
    const isLong = stop < entry; // sell-stop below entry for a long
    const oneR = Math.abs(entry - stop); // £ value of 1 R
    if (oneR === 0) return null;
    const target = isLong ? entry + targetR * oneR : entry - targetR * oneR;

    // Bar spans from stop to target (or further if current is beyond).
    const min = Math.min(stop, target, current);
    const max = Math.max(stop, target, current);
    const span = max - min;
    if (span === 0) return null;

    const pos = (price: number) => ((price - min) / span) * 100;

    const rMultiples = (price: number) => (price - entry) / (isLong ? oneR : -oneR);

    return {
      isLong,
      oneR,
      target,
      stopPos: pos(stop),
      entryPos: pos(entry),
      currentPos: pos(current),
      targetPos: pos(target),
      currentR: rMultiples(current),
      profitable: isLong ? current > entry : current < entry,
      atRisk: isLong ? current <= entry : current >= entry,
    };
  }, [entry, stop, current, targetR]);

  if (!view) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
        <h3 className="text-sm font-medium text-zinc-200 mb-1">R-multiple</h3>
        <p className="text-[11px] text-zinc-400">Set a stop-loss to see distance in units of R.</p>
      </div>
    );
  }

  const stopBandLeft = Math.min(view.stopPos, view.entryPos);
  const stopBandRight = Math.max(view.stopPos, view.entryPos);
  const targetBandLeft = Math.min(view.entryPos, view.targetPos);
  const targetBandRight = Math.max(view.entryPos, view.targetPos);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-zinc-200">R-multiple</h3>
        <span
          className={cn(
            'text-xs num',
            view.currentR >= 0 ? 'text-emerald-400' : 'text-rose-400',
          )}
        >
          {view.currentR >= 0 ? '+' : ''}
          {view.currentR.toFixed(2)}R
        </span>
      </div>
      <div className="relative h-8 bg-zinc-900/40 rounded-md border border-zinc-800">
        {/* Loss band: stop → entry */}
        <div
          className="absolute inset-y-0 bg-rose-500/20 border-r border-rose-500/30"
          style={{ left: `${stopBandLeft}%`, width: `${stopBandRight - stopBandLeft}%` }}
        />
        {/* Win band: entry → target */}
        <div
          className="absolute inset-y-0 bg-emerald-500/15"
          style={{ left: `${targetBandLeft}%`, width: `${targetBandRight - targetBandLeft}%` }}
        />
        {/* Stop marker */}
        <Marker pos={view.stopPos} color="bg-rose-500" label="Stop" />
        {/* Entry marker */}
        <Marker pos={view.entryPos} color="bg-zinc-500" label="Entry" />
        {/* Target marker */}
        <Marker pos={view.targetPos} color="bg-emerald-500" label={`${targetR}R`} />
        {/* Current marker — emphasised */}
        <Marker
          pos={view.currentPos}
          color={view.profitable ? 'bg-emerald-300' : 'bg-rose-300'}
          label="Now"
          big
        />
      </div>
      <div className="flex justify-between text-[10px] text-zinc-400 mt-1 num">
        <span>Stop {fmtNumber(stop ?? 0)}</span>
        <span>Entry {fmtNumber(entry)}</span>
        <span>Now {fmtNumber(current)} ({fmtSignedPercent(((current - entry) / entry) * 100)})</span>
        <span>{targetR}R {fmtNumber(view.target)}</span>
      </div>
    </div>
  );
}

function Marker({
  pos,
  color,
  label,
  big = false,
}: {
  pos: number;
  color: string;
  label: string;
  big?: boolean;
}) {
  return (
    <div
      className="absolute top-0 bottom-0 flex flex-col items-center"
      style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
    >
      <div className={cn('w-px h-full', color)} />
      <div className={cn('absolute -bottom-4', big ? 'text-[10px] font-medium text-zinc-100' : 'text-[9px] text-zinc-400')}>
        {label}
      </div>
    </div>
  );
}
